import type { Ficha as FichaDoContrato } from "../api"
import { Button } from "../componentes/Button"
import * as Icones from "../componentes/Icones"
import { SITUACOES, Selo, SeloDoValor, chaveDoNivel } from "../componentes/Selo"
import {
  data,
  documento,
  moeda,
  percentual,
  processoDoContrato,
  numeroComAno,
  rotulo,
} from "../formato"
import { CARTAO, MAIUSCULA, ROTULO_INDICADOR, TEXTO_DO_CADASTRO } from "../lib/classes"
import { cn } from "../lib/cn"

export function CabecaDaFicha({
  ficha,
  aoAbrirProcesso,
  aoIrParaDesignados,
  aoIrParaOcorrencias,
}: {
  ficha: FichaDoContrato
  aoAbrirProcesso: () => void
  aoIrParaDesignados: () => void
  aoIrParaOcorrencias: () => void
}) {
  const ultimoAditivo = ficha.aditivos
    .filter((a) => a.percentual_acumulado !== null)
    .slice(-1)[0]
  const semFiscal = ficha.fiscais.filter((f) => f.vigente).length === 0
  const segura = ficha.vigencia.definicao_segura

  return (
    <section className={cn(CARTAO, "@container px-5 py-4")}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-2">
        <div className="flex min-w-0 items-center gap-3">
          <p className={ROTULO_INDICADOR}>
            Contrato {numeroComAno(ficha.numero, ficha.ano)}
            <span aria-hidden="true" className="mx-1.5 text-border">•</span>
            Processo {processoDoContrato(ficha.processo_administrativo, ficha.origem_processo)}
          </p>
        </div>
        <SeloDoValor valor={ficha.situacao} rotulo={rotulo(ficha.situacao)} />
      </div>

      <div className="flex flex-col gap-2 py-4">
        <h1 className={cn("text-[1.1875rem] leading-snug font-semibold", TEXTO_DO_CADASTRO)}>{ficha.objeto}</h1>
        <p className="text-sm text-muted-foreground">
          <span className="mr-1.5 inline-flex align-[-2px]">
            <Icones.Empresa tamanho={14} />
          </span>
          Contratado: <span className={cn("text-foreground", TEXTO_DO_CADASTRO)}>{ficha.contratado.razao_social}</span>
          <span aria-hidden="true" className="mx-1.5 text-border">•</span>
          <span className="whitespace-nowrap tabular-nums">{documento(ficha.contratado.documento)}</span>
        </p>
      </div>

      <div className="flex flex-col gap-3 pb-4">
        <div data-slot="aviso-de-vigencia" className="flex flex-col gap-1 text-sm">
          {segura ? (
            <p className="font-medium">{fraseDoTempo(ficha)}</p>
          ) : (
            <p className="flex items-start gap-1.5 font-medium text-(--ambar)">
              <span className="mt-0.5 flex shrink-0">
                <Icones.Ressalva tamanho={16} />
              </span>
              {fraseDoTempo(ficha)}
            </p>
          )}
          {origemDoTermino(ficha) !== null && (
            <p className={cn("text-muted-foreground", !segura && "pl-[22px]")}>{origemDoTermino(ficha)}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!segura && <Selo situacao="vigencia_insegura" />}
          {ficha.criticidade !== null && ficha.criticidade !== undefined && (
            <Selo situacao={chaveDaCriticidade(ficha.criticidade)} />
          )}
          {semFiscal && <Selo situacao="sem_fiscal" />}
          {ficha.origem === "athus" && <Selo situacao="origem_athus" />}
        </div>
      </div>

      <dl data-slot="dados-da-ficha" className="grid grid-cols-1 gap-4 border-t border-border pt-4 @[26rem]:grid-cols-2 @[52rem]:grid-cols-4">
        <Dado rotulo="Valor inicial" valor={moeda(ficha.valor_inicial)} />
        <Dado rotulo="Valor atual" valor={moeda(ficha.valor_atual)} destaque />
        <Dado
          rotulo="Vigência"
          valor={`${data(ficha.vigencia_inicio)} a ${data(ficha.vigencia_fim)}`}
        />
        <Dado
          rotulo="Prorrogação"
          valor={ficha.prorrogavel === true ? "Prorrogável" : ficha.prorrogavel === false ? "Sem cláusula de prorrogação" : "—"}
          nota={ficha.prorrogavel === true && ficha.prorrogacao_limite ? `Até ${ficha.prorrogacao_limite}` : undefined}
        />
      </dl>

      {ultimoAditivo !== undefined && ultimoAditivo.limite_aplicado !== null && (
        <div
          className={cn(
            "mt-4 rounded-(--raio-controle) border border-primary bg-muted px-[15px] py-[13px] text-[0.8125rem] [&_p]:mt-1.5",
            ultimoAditivo.excedeu_o_limite && "border-destructive bg-destructive/10",
          )}
        >
          <strong>
            Acréscimos acumulados: {percentual(ultimoAditivo.percentual_acumulado)} ·
            limite aplicado: {percentual(ultimoAditivo.limite_aplicado)}
          </strong>
          {ultimoAditivo.excedeu_o_limite && (
            <p>
              O acumulado passou do limite. O ato foi{" "}
              <strong>registrado, não impedido</strong> — a verificação está
              configurada como alerta. Registre a motivação no processo.
            </p>
          )}
          {ultimoAditivo.limite_fundamento !== null && (
            <p className={cn("text-(length:--texto-xs) text-muted-foreground", MAIUSCULA)}>{ultimoAditivo.limite_fundamento}</p>
          )}
        </div>
      )}

      <div data-slot="atalhos-da-ficha" className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
        {ficha.pode_editar !== false && (
          <Button type="button" variant={semFiscal ? "default" : "outline"} size="sm" onClick={aoIrParaDesignados}>
            <Icones.Pessoa />
            Designar fiscal
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={aoAbrirProcesso}>
          <Icones.Abrir />
          Abrir o processo
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={aoIrParaOcorrencias}>
          <Icones.Anotacao />
          Registrar ocorrência
        </Button>
      </div>
    </section>
  )
}

function Dado({
  rotulo: nome,
  valor,
  nota,
  destaque = false,
}: {
  rotulo: string
  valor: string
  nota?: string
  destaque?: boolean
}) {
  return (
    <div data-slot="dado-da-ficha" className="min-w-0">
      <dt className={ROTULO_INDICADOR}>{nome}</dt>
      <dd className={cn("mt-[5px] text-[0.9375rem] tabular-nums", destaque && "text-(length:--texto-xl) font-[650]")}>{valor}</dd>
      {nota !== undefined && <dd className="text-xs text-muted-foreground">{nota}</dd>}
    </div>
  )
}

function chaveDaCriticidade(criticidade: string) {
  const chaves = {
    baixa: "criticidade_baixa",
    media: "criticidade_media",
    alta: "criticidade_alta",
    critica: "criticidade_critica",
  } as const
  return chaves[criticidade as keyof typeof chaves] ?? "criticidade_baixa"
}

function fraseDoTempo(ficha: FichaDoContrato): string {
  const { definicao_segura: segura, dias_para_vencer: dias, inicio } = ficha.vigencia
  if (!segura || dias === null) {
    return (
      "Sem definição segura de vigência — registre a publicação e valide a " +
      "memória na aba Vigência e prazos."
    )
  }
  const decorrido = inicio === null ? null : duracaoDesde(inicio)
  const comeco = decorrido === null ? "Vigência" : `Vigente há ${decorrido}`
  if (dias < 0) return `${comeco} · vencido há ${emDias(-dias)}`
  if (dias === 0) return `${comeco} · vence hoje`
  return `${comeco} · vence em ${emDias(dias)}`
}

function origemDoTermino(ficha: FichaDoContrato): string | null {
  const { fonte_da_data: fonte, referencia, nivel } = ficha.vigencia
  if (fonte === null) return null
  const quando = referencia === null ? "" : `: ${data(referencia)}`
  const grau = nivel === null ? "" : ` • ${SITUACOES[chaveDoNivel(nivel)].rotulo}`
  return `Data de término lida da ${fonte}${quando}${grau}`
}

function emDias(quantos: number): string {
  return quantos === 1 ? "1 dia" : `${quantos} dias`
}

function duracaoDesde(inicio: string): string | null {
  const partida = new Date(`${inicio}T00:00:00`)
  if (Number.isNaN(partida.getTime())) return null
  const hoje = new Date()
  if (partida > hoje) return null

  let anos = hoje.getFullYear() - partida.getFullYear()
  let meses = hoje.getMonth() - partida.getMonth()
  let dias = hoje.getDate() - partida.getDate()
  if (dias < 0) {
    meses -= 1
    dias += new Date(hoje.getFullYear(), hoje.getMonth(), 0).getDate()
  }
  if (meses < 0) {
    anos -= 1
    meses += 12
  }
  const partes: string[] = []
  if (anos > 0) partes.push(`${anos} ano${anos > 1 ? "s" : ""}`)
  if (meses > 0) partes.push(`${meses} ${meses > 1 ? "meses" : "mês"}`)
  if (dias > 0 || partes.length === 0)
    partes.push(`${dias} dia${dias === 1 ? "" : "s"}`)
  return partes.join(" ")
}
